import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';

import * as crc32 from 'crc-32';
import { digitsEnToFa } from '@persian-tools/persian-tools';
import { ActivatedRoute } from '@angular/router';
import { printOrderAnimations } from 'src/app/feature/landing/animation';
import { StockItem } from 'src/app/core';
import {
  CreateBlockOptions,
  PrintBlockInputNewDataEvent,
  PrintBlockItem,
  rangeValidator,
} from 'src/app/shared';
import { PrintBlockService } from 'src/app/feature/services';

@Component({
  selector: 'shared-print-block-single',
  templateUrl: './print-block-single.component.html',
  styleUrls: ['./print-block-single.component.sass'],
  animations: [printOrderAnimations],
})
export class PrintBlockSingleInputComponent implements OnInit, OnChanges {
  @Input('placeholder') placeholder: string = '';
  @Input('isSingle') isSingle: boolean = false;
  @Input('limit') limit!: number | undefined;
  @Input('type') type!: StockItem;
  @Input('printBlockItems') items!: PrintBlockItem[];
  @Output('dataUpdate') dataUpdate!: EventEmitter<PrintBlockInputNewDataEvent>;

  // Listeners --------------------------------------------------------------->
  @HostListener('document:click', ['$event'])
  clickEvent(event: Event) {
    if (!this.eRef.nativeElement.contains(event.target) && this.addInputOpen) {
      this.toggleInputOverlay();
    }
  }

  private _subscriptions: Subscription[] = [];
  private set subscriptions(subscription: Subscription | undefined) {
    if (subscription) {
      this._subscriptions.push(subscription);
    }
  }

  private itemsKey: string[] = [];
  public addInputOpen: boolean = false;
  public singleFormControl = new FormControl();
  public rangeFormControl = new FormControl('', rangeValidator);

  // Constructors ------------------------------------------------------------>

  async ngOnInit(): Promise<void> {}
  async ngOnChanges(): Promise<void> {}

  constructor(
    private printBlockService: PrintBlockService,
    private eRef: ElementRef,

    activatedRoute: ActivatedRoute
  ) {
    this.dataUpdate = new EventEmitter();
    activatedRoute.data.subscribe((data) => {
      if (data) {
        if (data['order']) {
          this.items = (data['order']?.file?.fileBlocks || []).filter(
            (block: PrintBlockItem) => {
              return block?.stock?.type === this.type?.type;
            }
          );
        }
      }
    });
  }

  // Public items ------------------------------------------------------------>

  public addNewItem(data: string | number) {
    if (!data) return;

    const options = this.createOptionsForBlockCreation(data);
    const block = this.printBlockService.createBlock(options);

    if (!this.ifNotExists(block)) return;

    this.storeItemBlock(block);
    this.emitDataChangeEvent();

    this.singleFormControl.reset();
    this.rangeFormControl.reset();
  }

  public removeItem(index: number) {
    const hashKey = this.generateBlockHash(this.items[index]);

    this.itemsKey.splice(this.itemsKey.indexOf(hashKey.toString()), 1);
    this.items.splice(index, 1);
    this.emitDataChangeEvent();
  }

  public toFaCharacters(...args: string[]) {
    const string = args.join(' ');
    return digitsEnToFa(string);
  }

  public toggleInputOverlay() {
    this.addInputOpen = !this.addInputOpen;
  }

  // Private methods ------------------------------------------------------------>

  private ifNotExists(block: PrintBlockItem) {
    const hashKey = this.generateBlockHash(block);
    const index = this.itemsKey.indexOf(hashKey.toString());

    if (index < 0) {
      return true;
    }
    return false;
  }

  private createOptionsForBlockCreation(
    data: string | number
  ): CreateBlockOptions {
    return { data, type: this.type };
  }

  private emitDataChangeEvent() {
    this.dataUpdate.emit({
      data: this.items,
      type: this.type,
    });
  }

  private storeItemBlock(block: PrintBlockItem) {
    const hashKey = this.generateBlockHash(block);

    this.itemsKey.push(hashKey.toString());
    this.items.push(block);
  }

  private generateBlockHash(block: PrintBlockItem): number {
    return crc32.str(JSON.stringify(block));
  }

  ngOnDestroy() {
    this._subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}
